/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import {
  ServiceTypeGroup,
  ServiceTypeGroups,
} from "@/models/api/administration/servicesAndAttributes/ServiceTypeGroup";
import {
  addServiceGroup,
  assignFilteredServiceTypeToServiceTypeGroup,
  assignServiceTypeToServiceTypeGroup,
  editServiceGroup,
  getServiceForGroup,
  getServiceTypeGroups,
  isAnyServiceInServiceGroup,
  removeServiceTypeGroup,
  unassignAllServiceTypeFromServiceTypeGroup,
  unassignServiceTypeFromServiceTypeGroup,
} from "@/requests/administration/servicesAndAttributes/administrationServiceGroups";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DataOptions } from "vuetify";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import _ from "lodash";
import { FILTER_NAMES } from "@/static/filterNames";
import {
  ServiceTypeGroupItem,
  ServiceTypeGroupItems,
} from "@/models/api/administration/servicesAndAttributes/ServiceTypeGroupItems";
import { NewServiceTypeGroup } from "@/models/api/administration/servicesAndAttributes/NewServiceTypeGroup";

export interface AdministrationServiceGroupsStateInterface {
  serviceTypeGroups: Array<ServiceTypeGroup>;
  selectedServiceGroupId: null | number;
  services: Array<ServiceTypeGroupItem>;
  servicesGroupFilters: null | Array<CriterionBase>;
  servicesFilters: null | Array<CriterionBase>;
  servicesCount: null | number;
  serviceTypeGroupsCount: null | number;
  servicesGroupDataTableOptions: DataOptions;
  servicesDataTableOptions: DataOptions;
  isAddDialog: boolean;
  newServiceTypeGroup: NewServiceTypeGroup;
  serviceGroupToEdit: ServiceTypeGroup | null;
  displayedName: string;
}

export const AdministrationServiceGroupsState: AdministrationServiceGroupsStateInterface = {
  serviceTypeGroups: [],
  selectedServiceGroupId: null,
  services: [],
  servicesFilters: null,
  servicesGroupFilters: null,
  servicesCount: null,
  serviceTypeGroupsCount: null,
  servicesDataTableOptions: new DefaultDataOptions(),
  servicesGroupDataTableOptions: new DefaultDataOptions(),
  isAddDialog: false,
  newServiceTypeGroup: new NewServiceTypeGroup(),
  serviceGroupToEdit: null,
  displayedName: "",
};

const AdministrationServiceGroupsModule = defineModule({
  state: AdministrationServiceGroupsState as AdministrationServiceGroupsStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setServiceTypeGroups(state, param: ServiceTypeGroups) {
      state.serviceTypeGroups = param.items;
      state.serviceTypeGroupsCount = param.totalCount;
    },
    setSelectedServiceGroupId(state, param: number | null) {
      state.selectedServiceGroupId = param;
    },
    setGroupFilters(state, param: null | Array<CriterionBase>) {
      state.servicesGroupFilters = param;
    },
    setFilters(state, param: null | Array<CriterionBase>) {
      state.servicesFilters = param;
    },
    setIsAddDialog(state, param: boolean) {
      state.isAddDialog = param;
    },
    setDialogServiceGroup(state, param: NewServiceTypeGroup) {
      state.newServiceTypeGroup = param;
    },
    setServices(state, param: ServiceTypeGroupItems) {
      state.services = param.items;
      state.servicesCount = param.totalCount;
    },
    setServicesDataTableOptions(state, param: DataOptions) {
      state.servicesDataTableOptions = param;
    },
    setServicesGroupDataTableOptions(state, param: DataOptions) {
      state.servicesGroupDataTableOptions = param;
    },
    setServiceGroupToEdit(state, param: ServiceTypeGroup | null) {
      state.serviceGroupToEdit = param;
      if (param) state.displayedName = param.displayedName;
      else state.displayedName = "";
    },
    setDisplayedName(state, param: string) {
      state.displayedName = param;
    },
  },
  actions: {
    async downloadServiceTypeGroups(context, skipSetDefault: boolean | undefined) {
      const { commit, state } = _moduleActionContext(context);
      const serviceTypeGroups = await getServiceTypeGroups({
        count: state.servicesGroupDataTableOptions.itemsPerPage,
        page: state.servicesGroupDataTableOptions.page - 1,
        criterions: _.cloneDeep(state.servicesGroupFilters),
        filterName: FILTER_NAMES.ServiceTypeGroupFilter,
      });
      commit.setServiceTypeGroups(serviceTypeGroups);
      if (!skipSetDefault && serviceTypeGroups.items?.length > 0) {
        commit.setSelectedServiceGroupId(serviceTypeGroups.items[0].id);
      }
    },
    async downloadServices(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.selectedServiceGroupId !== null) {
        const services = await getServiceForGroup(
          {
            count: state.servicesDataTableOptions.itemsPerPage,
            page: state.servicesDataTableOptions.page - 1,
            criterions: _.cloneDeep(state.servicesFilters),
            filterName: FILTER_NAMES.ServiceTypeGroupItemFilter,
          },
          state.selectedServiceGroupId
        );
        commit.setServices(services);
      }
    },
    async openAddServiceGroupDialog(context) {
      const { commit } = _moduleActionContext(context);
      commit.setIsAddDialog(true);
    },
    async saveServiceGroup(context) {
      const { state, commit, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      const id = await addServiceGroup(state.newServiceTypeGroup);
      commit.setIsAddDialog(false);
      await dispatch.downloadServiceTypeGroups(true);
      commit.setSelectedServiceGroupId(id);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async editServiceGroup(context, name: string) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (state.serviceGroupToEdit) {
        await editServiceGroup({ id: state.serviceGroupToEdit.id, displayedName: name });
        await dispatch.downloadServiceTypeGroups(false);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async removeServiceGroup(context, serviceGroupId: number) {
      const { commit, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      const hasAnyService = await isAnyServiceInServiceGroup(serviceGroupId);

      const confirm = new ConfirmModel(
        filterCapitalize(
          i18n.t(hasAnyService ? "doYouWantToRemoveServiceGroupWithServices" : "doYouWantToRemoveServiceGroup")
        ),
        filterCapitalize(i18n.t("warning")),
        null,
        "500px"
      );

      const isConfirmed = await rootDispatch.popup.addConfirm(confirm);
      if (isConfirmed) {
        await removeServiceTypeGroup(serviceGroupId);
        await rootDispatch.popup.addOperationSuccessfulSnackbar();
        commit.setSelectedServiceGroupId(null);
        await dispatch.downloadServiceTypeGroups();
      }
    },
    async toggleAssignment(context, serviceGroupItem: ServiceTypeGroupItem) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (state.selectedServiceGroupId !== null) {
        if (!serviceGroupItem.isAssignedToService) {
          await assignServiceTypeToServiceTypeGroup(state.selectedServiceGroupId, serviceGroupItem.id);
        } else {
          await unassignServiceTypeFromServiceTypeGroup(state.selectedServiceGroupId, serviceGroupItem.id);
        }
        await rootDispatch.popup.addOperationSuccessfulSnackbar();
        await dispatch.downloadServices();
      }
    },
    async unassignAllServiceTypes(context) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (state.selectedServiceGroupId !== null) {
        if (
          await rootDispatch.popup.addConfirm(
            ConfirmModel.confirmBoolFromContent(filterCapitalize(i18n.t("doYouReallyWantToUnassignAllServiceTypes")))
          )
        ) {
          await unassignAllServiceTypeFromServiceTypeGroup(state.selectedServiceGroupId);
          rootDispatch.popup.addOperationSuccessfulSnackbar();
          await dispatch.downloadServices();
        }
      }
    },
    async assignFilteredServiceTypes(context) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (state.selectedServiceGroupId !== null) {
        if (
          await rootDispatch.popup.addConfirm(
            ConfirmModel.confirmBoolFromContent(filterCapitalize(i18n.t("doYouReallyWantToAssignAllServiceTypes")))
          )
        ) {
          await assignFilteredServiceTypeToServiceTypeGroup(state.selectedServiceGroupId, {
            count: state.servicesDataTableOptions.itemsPerPage,
            page: state.servicesDataTableOptions.page - 1,
            criterions: _.cloneDeep(state.servicesFilters),
            filterName: FILTER_NAMES.ServiceTypeGroupItemFilter,
          });
          rootDispatch.popup.addOperationSuccessfulSnackbar();
          await dispatch.downloadServices();
        }
      }
    },
  },
});

export default AdministrationServiceGroupsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationServiceGroupsModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationServiceGroupsModule);
